// Tomtilstander, feiltilstander og ventetilstander.
//
// HVORFOR DETTE ER EN EGEN FIL, OG DEN VIKTIGSTE I UI-DELEN AV PAKKEN:
// en sensorflate bygges alltid FØR broen som skal mate den. Alt her er derfor
// det operatøren FAKTISK ser første gang, og det avgjør om flata er ærlig eller
// «mockup og tull» (UX_DESIGNSYSTEM §10, grunnloven §1). Regelen vi holder oss
// til:
//
//   En tom liste skal si HVA SOM MANGLER og HVA SOM SKJER NÅR DET KOMMER.
//   «Ingen kontakter» er en løgn hvis sannheten er «vi har ingen radarbro».
//
// ══ HIERARKIET I «MANGLER KILDE», OG HVORFOR DET BLE SNUDD ══════════════════
// Første utgave av `manglerKilde` viste den EKSAKTE adressen vi forsøkte, med den
// begrunnelsen at en operatør som kan lese adressen kan si «den står feil» til
// drift. Halvparten av det holder fortsatt: variabelnavnet SKAL stå, drift
// trenger det. Men i praksis fikk appene inn `'(VITE_ULL_WS er ikke satt)'` som
// «adresse», og da var det EN MILJØVARIABEL som dominerte skjermbildet mens
// forklaringen sto i en mindre linje under.
//
// Et variabelnavn er ikke hjelp til en operatør. Han kan ikke sette det, og han
// kan ikke lese noe operativt ut av det. Hierarkiet er derfor snudd:
//
//   1. NORSK forklaring FØRST, som tittel: «Ingen sensorbro er satt opp …»
//   2. Hva flata VIL vise når kilden finnes.
//   3. NESTE STEG — hva operatøren gjør nå (be drift sette den opp, øve i mens).
//   4. Teknisk detalj, DEMPET, sist: variabelnavnet og adressen for drift.
import { div, h, ikonlinje, knapp, p, span } from './dom.js';
import { ikon, somIkon } from './ikoner.js';
/** Skjelett mens vi venter. Antyder formen på det som kommer, uten å påstå tall. */
export function laster(tekst = 'Kobler til …', linjer = 3) {
    const rot = div('tilstand tilstand-laster');
    rot.setAttribute('aria-busy', 'true');
    rot.append(span('tilstand-tekst', tekst));
    const skjelett = div('skjelett');
    for (let i = 0; i < linjer; i++)
        skjelett.append(div('skjelett-linje'));
    rot.append(skjelett);
    return rot;
}
/**
 * Tomtilstand: dempet linjeikon, én linje veiledning, eventuelt en inline-CTA
 * (§8 — «ingen naken grå tekst»).
 */
export function tomt(tittel, veiledning, valg = {}) {
    const rot = div('tilstand tilstand-tom');
    rot.append(somIkon(valg.ikon ?? 'info'));
    rot.append(h(3, 'tilstand-tittel', tittel));
    rot.append(p('tilstand-brød', veiledning));
    if (valg.handling) {
        rot.append(knapp(valg.handling.tekst, valg.handling.gjør, { variant: 'primær' }));
    }
    return rot;
}
/**
 * «Denne kilden finnes ikke ennå» — den ærlige tomtilstanden.
 *
 * Den skiller seg visuelt fra en driftsfeil (info-ramme, ikke rød) fordi det
 * IKKE er en feil: broen er ikke bygget/koblet opp. Å farge den rød ville sendt
 * operatøren på feilsøking etter noe som ikke er i stykker.
 *
 * Rekkefølgen i noden er hierarkiet fra filhodet, og den er hele endringen: hva
 * som er sant, hva som kommer, hva du gjør nå — og først DERETTER hva drift skal
 * skru på.
 */
export function manglerKilde(valg) {
    const rot = div('tilstand tilstand-mangler');
    const topp = div('tilstand-topp');
    topp.append(ikon('info'));
    // Forklaringen er TITTELEN. Det er den ene setningen som må leses, og en
    // tittel leses selv av den som bare kaster et blikk på panelet.
    topp.append(h(3, 'tilstand-tittel', valg.forklaring));
    rot.append(topp);
    rot.append(p('tilstand-brød', valg.hvaKommer));
    if (valg.neste)
        rot.append(p('tilstand-neste', valg.neste));
    if (valg.handling) {
        rot.append(knapp(valg.handling.tekst, valg.handling.gjør, { variant: 'primær' }));
    }
    if (valg.teknisk)
        rot.append(p('tilstand-detalj', valg.teknisk));
    return rot;
}
/** Driftsfeil: noe som SKULLE virket, virker ikke. Rød ramme, `role=alert`. */
export function feilboks(tittel, melding, detalj, påNytt) {
    const rot = div('tilstand tilstand-feil');
    rot.setAttribute('role', 'alert');
    const topp = div('tilstand-topp');
    topp.append(ikon('varsel'));
    topp.append(h(3, 'tilstand-tittel', tittel));
    rot.append(topp);
    rot.append(p('tilstand-brød', melding));
    if (detalj)
        rot.append(p('tilstand-detalj', detalj));
    if (påNytt)
        rot.append(knapp('Prøv igjen', påNytt, { variant: 'stille', ikon: ikon('last') }));
    return rot;
}
/**
 * ØVINGSBANNERET — krav 2 i `ovelse.ts`, og grunnloven §1 ordrett: øvings-/
 * demo-modus skal merkes så tydelig at et skjermbilde ALDRI kan forveksles med
 * et ekte situasjonsbilde.
 *
 * ══ HVORFOR RØDT OG IKKE GULL ══════════════════════════════════════════════
 * Første utgave var gull (`--olive`/`--gold`), altså flatens aksentfarge. Det var
 * feil av to grunner. Gull brukes ellers til «aktiv/uthevet» på denne flata, så
 * banneret leste som et vanlig uthevet felt. Og COP — som er den flata folk
 * kjenner — merker det samme med `--danger`: «DEMO — IKKE EKTE DATA» som en rød
 * pille i topplinja (`.demo-chip` i `apps/cop/src/style.css`). Ett program skal
 * ha ETT språk for «dette er ikke ekte». Derfor rødt, versaler, øverst.
 *
 * ══ HVORFOR BANNERET IKKE KAN LUKKES ═══════════════════════════════════════
 * Knappen slår av ØVINGEN, den skjuler ikke banneret. Et banner man kan klikke
 * bort, blir klikket bort — og da står en flate full av oppdiktede kontakter
 * uten et eneste merke. `settOvelsesramme` er den andre halvparten: en ramme
 * rundt hele flata, slik at også et UTSNITT av skjermen bærer merket.
 *
 * `tekst` kan overstyres fordi det er appen som vet hva den dikter opp
 * («kontaktene», «nodene», «utløsningene»). Ordene «ØVINGSDATA — IKKE EKTE DATA»
 * skal alltid stå først, uansett hva som følger.
 */
export function ovelsebanner(av, tekst = 'ØVINGSDATA — IKKE EKTE DATA. Alt under er oppdiktet, og ingenting kan sendes til SKADE.') {
    const rot = div('ovelsebanner');
    // `role=alert` og ikke `status`: dette skal LESES OPP av skjermleseren i det
    // øvingen starter, ikke ligge og vente på at operatøren flytter fokus dit.
    rot.setAttribute('role', 'alert');
    rot.append(ikonlinje('varsel', tekst, 'ovelsebanner-tekst'));
    rot.append(knapp('Slå av øvingsdata', av, { variant: 'stille' }));
    return rot;
}
/**
 * ØVINGSRAMMA — den andre, permanente merkingen.
 *
 * Setter (eller fjerner) `ovelse-aktiv` på `<body>`, og stilarket tegner en
 * ramme i `--danger` rundt hele viewporten. Grunnen til at den finnes I TILLEGG
 * til banneret er konkret: et skjermbilde av ET PANEL — som er nettopp det folk
 * limer inn i en rapport eller sender over sambandet — inneholder ikke toppen av
 * siden. Ramma gjør at hvert utsnitt bærer merket.
 *
 * Den ligger på `body` og ikke på flate-elementet fordi et poppet-ut panel
 * fjerner skallet (se `montérPoppetPanel`) — `body` er det ene elementet som
 * finnes i begge tilfeller.
 */
export function settOvelsesramme(på) {
    document.body.classList.toggle('ovelse-aktiv', på);
}
export function lagKunngjører() {
    const rot = div('kunngjøring');
    rot.setAttribute('aria-live', 'polite');
    rot.setAttribute('aria-atomic', 'true');
    return {
        rot,
        si(melding) {
            // Tømming først: samme tekst to ganger på rad leses ellers bare én gang,
            // og da tror operatøren at den andre handlingen ikke gikk gjennom.
            rot.textContent = '';
            globalThis.setTimeout(() => { rot.textContent = melding; }, 50);
        },
    };
}
/** Kort kvittering nede i hjørnet på noe operatøren nettopp gjorde. */
export function kvittering(melding, variant = 'ok') {
    const boks = div(`kvittering kvittering-${variant}`);
    boks.append(ikon(variant === 'ok' ? 'sjekk' : 'varsel'), span('', melding));
    document.body.append(boks);
    globalThis.setTimeout(() => { boks.classList.add('ut'); }, 3200);
    globalThis.setTimeout(() => { boks.remove(); }, 3600);
}
//# sourceMappingURL=tilstand.js.map